import { Injectable } from '@angular/core';

import Highcharts from 'highcharts';
import {
  RGBAColor,
  RGBAColorInput,
} from '@inmoment/the-kitchen/src/lib/Pickers/ColorPicker/ColorPicker.model';

import { Widget } from 'app/providers/bridge-api/dashboard.service';
import { EnableClassTelemetry } from 'app/services/otel.service';
import { HighchartsOptions } from '../../widget/services/chart-display.service';

export interface CustomItemColor {
  pointId: string;
  color: string;
}

@EnableClassTelemetry()
@Injectable()
export class ChartCustomColorService {
  itemColorDictionary: CustomItemColor[] = [];
  chartOptionsDictionary: HighchartsOptions[] = [];
  constructor() {}

  addChartOptionsToDictionary(widgetId: string, chartOptions: HighchartsOptions) {
    const foundIndex = this.chartOptionsDictionary.findIndex((item) => item.widgetId === widgetId);
    if (foundIndex !== -1) {
      this.chartOptionsDictionary[foundIndex] = { widgetId, chartOptions };
    } else {
      this.chartOptionsDictionary.push({ widgetId, chartOptions });
    }
  }

  getWidgetChartOptions(widgetId: string) {
    return this.chartOptionsDictionary.find((item) => item.widgetId === widgetId);
  }

  // Add new color to each widgets payload if item present in chart
  public syncWidgetColor(widget: Widget, color: CustomItemColor) {
    const chartOptions = this.getWidgetChartOptions(widget.id);
    // If color picking n/a for this widget
    if (!chartOptions) {
      return {
        widgetPayloadCustomColors: null,
        isComponentDirty: false,
      };
    }

    const chartItems = this.getWidgetItemsLocation(
      widget,
      chartOptions.chartOptions,
      color.pointId
    );
    let foundChartItemIndex: number;
    const graphType = widget.payload.graphType;
    if (graphType === 'spline' || graphType === 'line' || graphType === 'scatter') {
      foundChartItemIndex = chartItems.findIndex(
        (item) => item.custom?.seriesId === color.pointId || item.name === color.pointId
      );
    } else {
      foundChartItemIndex = chartItems.findIndex(
        (item) => item.pointId === color.pointId || item.id === color.pointId
      );
    }

    if (foundChartItemIndex !== -1) {
      const customItemColorsArray = this.parseCustomColorsPayload(widget.payload.customItemColors);

      const foundPayloadIndex = customItemColorsArray.findIndex(
        (item) => item?.pointId === color.pointId
      );

      if (foundPayloadIndex !== -1) {
        customItemColorsArray[foundPayloadIndex] = color;
      } else {
        customItemColorsArray.push(color);
      }

      const customItemColorsPayload = [];
      for (const item of customItemColorsArray) {
        customItemColorsPayload.push(JSON.stringify(item));
      }

      return { widgetPayloadCustomColors: customItemColorsPayload, isComponentDirty: true };
    } else {
      return {
        widgetPayloadCustomColors: widget.payload.customItemColors,
        isComponentDirty: false,
      };
    }
  }

  // Add to/update widget payload custom colors from dictionary
  public getItemColorsFromDictionary(widget: Widget) {
    const customItemColorsArray = this.parseCustomColorsPayload(widget.payload.customItemColors);

    for (const color of this.itemColorDictionary) {
      const foundIndex = customItemColorsArray.findIndex((item) => item?.pointId === color.pointId);

      if (foundIndex !== -1) {
        customItemColorsArray[foundIndex] = color;
      } else {
        customItemColorsArray.push(color);
      }
    }

    const customItemColorsPayload = [];
    for (const item of customItemColorsArray) {
      customItemColorsPayload.push(JSON.stringify(item));
    }

    return customItemColorsPayload;
  }

  private getWidgetItemsLocation(widget: Widget, chartOptions: HighchartsOptions, pointId: string) {
    let chartItems = [];
    if (chartOptions) {
      switch (widget.payload.graphType) {
        case 'pie': {
          chartOptions.plotOptions = chartOptions.plotOptions ?? {};
          chartOptions.plotOptions.pie = chartOptions.plotOptions.pie ?? {};
          chartOptions.plotOptions.pie.colors = chartOptions.plotOptions.pie.colors ?? [];
          chartItems = chartOptions.series[0].data;
          break;
        }
        case 'spline':
        case 'line': {
          chartItems = chartOptions.series;
          break;
        }
        case 'column':
        case 'bar': {
          for (const s of chartOptions.series) {
            for (const d of s.data) {
              if (d.pointId === pointId || d.id === pointId) {
                //need double check b/c of secondary axis stuff
                chartItems.push(d);
              }
            }
          }
          break;
        }
        case 'scatter': {
          const series = chartOptions.series.find(
            (s) => s.custom?.seriesId === pointId || s.name === pointId
          );
          if (series) {
            chartItems.push(series);
          }
          break;
        }
        default: {
          chartItems = chartOptions.series[0].data;
          break;
        }
      }
    }
    return chartItems;
  }

  // Add to dictionary from payload if not present in dictionary
  public updateDictionaryItems(widget: Widget): void {
    const customItemColorsArray = this.parseCustomColorsPayload(widget.payload.customItemColors);

    for (const colorObject of customItemColorsArray) {
      if (!this.itemColorDictionary.some((item) => item.pointId === colorObject.pointId)) {
        this.itemColorDictionary.push(colorObject);
      }
    }
  }

  public addColorToDictionary(colorObject: CustomItemColor): void {
    const foundIndex = this.itemColorDictionary.findIndex(
      (item) => item.pointId === colorObject.pointId
    );
    if (foundIndex === -1) {
      this.itemColorDictionary.push(colorObject);
    } else {
      this.itemColorDictionary[foundIndex] = colorObject;
    }
  }

  public removeColorFromDictionaryByPointId(pointId: string): void {
    const index = this.itemColorDictionary.findIndex((item) => item.pointId === pointId);
    if (index > -1) {
      this.itemColorDictionary.splice(index, 1);
    }
  }

  public removeWidgetColorForPoint(widget: Widget, pointId: string): boolean {
    const customItemColorsArray = this.parseCustomColorsPayload(
      widget.payload.customItemColors ?? []
    );
    const foundIndex = customItemColorsArray.findIndex((item) => item?.pointId === pointId);
    if (foundIndex === -1) {
      return false;
    }
    customItemColorsArray.splice(foundIndex, 1);
    widget.payload.customItemColors = customItemColorsArray.map((item) => JSON.stringify(item));
    return true;
  }

  public removeColorsFromDictionary(customItemColors: string[]): void {
    const colorObjects: CustomItemColor[] = customItemColors.map((c) => JSON.parse(c));
    for (const colorObject of colorObjects) {
      const index = this.itemColorDictionary.findIndex(
        (item) => item.pointId === colorObject.pointId
      );
      if (index > -1) {
        this.itemColorDictionary.splice(index, 1);
      }
    }
  }

  public resetColorDictionary(): void {
    this.itemColorDictionary = [];
  }

  public setChartItemColors(widget: Widget, chartOptions: HighchartsOptions): HighchartsOptions {
    if (!widget.payload['customItemColors']) {
      widget.payload['customItemColors'] = [];
    } else {
      const customItemColorsArray = this.parseCustomColorsPayload(widget.payload.customItemColors);

      switch (widget.payload.graphType) {
        // Pie
        case 'pie': {
          let pieIndex = 0;
          const chartItems = chartOptions.series[0]?.data;
          if (chartItems?.length) {
            const colorPalette = Highcharts.getOptions().colors;
            for (let i = 0; i < chartItems.length; i++) {
              const chartItem = chartItems[i];
              const colorItem = customItemColorsArray.find(
                (item) => item?.pointId === chartItem?.pointId
              );
              if (pieIndex === colorPalette.length) {
                pieIndex = 0;
              }
              // Color pie slice with custom color, otherwise color with next default color item
              if (colorItem) {
                chartItem.color = colorItem.color;
              } else {
                chartItem.color = colorPalette[pieIndex];
              }
              pieIndex++;
            }
          }

          break;
        }
        // Spline/line
        case 'spline':
        case 'line': {
          for (let i = 0; i < chartOptions.series.length; i++) {
            const splineColorItem = customItemColorsArray.find(
              (item) =>
                item?.pointId === chartOptions.series[i]?.custom?.seriesId ||
                item?.pointId === chartOptions.series[i]?.name
            );
            if (splineColorItem) {
              chartOptions.series[i].color = splineColorItem.color;
            }
          }
          break;
        }
        case 'column':
        case 'bar': {
          const assignColorToData = (data, colorsArray: Array<any>, pointKey = 'pointId') => {
            for (let i = 0; i < data.length; i++) {
              const colorItem = colorsArray.find((item) => item?.pointId === data[i][pointKey]);
              if (colorItem) {
                data[i].color = colorItem.color;
              }
            }
          };

          if (chartOptions.series.length === 1 || typeof widget.payload.chartGoal === 'number') {
            assignColorToData(chartOptions.series[0].data, customItemColorsArray, 'pointId');
          } else if (chartOptions.series.length > 1) {
            for (const serie of chartOptions.series) {
              assignColorToData(serie.data, customItemColorsArray, 'id');
            }
          } else {
            for (const s of chartOptions.series) {
              const colorItem = customItemColorsArray.find(
                (item) => item?.pointId === s.custom?.seriesId || item?.pointId === s.name
              );
              if (colorItem) {
                s.color = colorItem.color;
              }
            }
          }
          break;
        }
        case 'scatter': {
          customItemColorsArray.forEach((c) => {
            const series = chartOptions.series.find(
              (s) => s.custom?.seriesId === c.pointId || s.name === c.pointId
            );
            if (series) {
              series.color = c.color;
            }
          });
          break;
        }
        default: {
          if (chartOptions.series[0]) {
            const chartItems = chartOptions.series[0].data;
            for (let i = 0; i < chartItems.length; i++) {
              const colorItem = customItemColorsArray.find(
                (item) => item?.pointId === chartItems[i]?.pointId
              );
              if (colorItem) {
                chartItems[i].color = colorItem.color;
              }
            }
          }
          break;
        }
      }
    }

    return chartOptions;
  }

  public initializeCustomColorsProperty(widget) {
    if (!widget.payload['customItemColors']) {
      widget.payload['customItemColors'] = [];
    }
    return widget;
  }

  private parseCustomColorsPayload(customItemColorsPayload): CustomItemColor[] {
    const customItemColorsArray = [];
    for (const item of customItemColorsPayload) {
      if (item) {
        customItemColorsArray.push(JSON.parse(item));
      }
    }
    return customItemColorsArray;
  }

  public getItemColor(
    widget: Widget,
    pointId: string,
    chartOptions: HighchartsOptions,
    chartColors: { itemColors: string[]; seriesColors: string[] }
  ): RGBAColorInput | undefined {
    const customColorsArray = this.parseCustomColorsPayload(widget.payload.customItemColors);

    let foundColor = customColorsArray.find((element) => element.pointId === pointId)?.color;
    if (!foundColor) {
      foundColor = this.getItemComputedColor(widget, pointId, chartOptions, chartColors);
    }
    if (foundColor) {
      return ChartCustomColorService.colorStringToRgbaColor(foundColor);
    }
    // We could only reach this if we don't have any custom colors set and we have to return undefined | string | RGBAColorInput
    return foundColor as undefined;
  }

  private getItemComputedColor(
    widget: Widget,
    pointId: string,
    chartOptions: HighchartsOptions,
    chartColors: { itemColors: string[]; seriesColors: string[] }
  ) {
    const getSeriesColor = () => {
      const itemSeriesIdx = chartOptions.series.findIndex(
        (s) => pointId === s.custom?.seriesId || pointId === s.name
      );

      if (itemSeriesIdx === -1) {
        return;
      }

      return chartOptions.series[itemSeriesIdx].color ?? chartColors.seriesColors[itemSeriesIdx];
    };

    const getDataColor = () => {
      const itemIdx = chartOptions.series[0]?.data.findIndex((d) => pointId === d.pointId) ?? -1;
      if (itemIdx === -1) {
        return;
      }

      return chartOptions.series[0].data[itemIdx].color ?? chartColors.itemColors[itemIdx];
    };

    switch (widget.payload.graphType) {
      // Spline/line
      case 'spline':
      case 'line':
      case 'scatter': {
        return getSeriesColor();
      }
      case 'column': {
        if (chartOptions.series.length === 1 || typeof widget.payload.chartGoal === 'number') {
          return getDataColor();
        } else {
          return getSeriesColor();
        }
      }
      default: {
        return getDataColor();
      }
    }
  }

  static colorStringToRgbaColor(color: string): RGBAColor {
    const rgba = color
      .replace(/^(rgb|rgba)\(/i, '')
      .replace(/\)$/, '')
      .replace(/\s/g, '')
      .split(',');

    return {
      red: parseInt(rgba[0], 10),
      green: parseInt(rgba[1], 10),
      blue: parseInt(rgba[2], 10),
      alpha: parseFloat(rgba[3] ?? '1'),
    };
  }

  static rgbaColorToColorString(color: RGBAColor): string {
    return `rgba(${color.red},${color.green},${color.blue},${color.alpha})`;
  }
}
